<template>
    <ContentPage class="home-page" :show-header="false">
        <section class="hero" id="jouer">
            <div class="hero__copy">
                <p class="eyebrow">Le geoguess de Montréal</p>
                <h1 class="hero__title">
                    <span class="hero__wordmark">✱ CARTOMTL</span>
                </h1>
                <p class="lede">
                    Devinez où vous êtes à Montréal dans Google Street View
                </p>
                <div class="hero-follow" aria-label="Me suivre (rapide)">
                    <span class="hero-follow__label">Par Nicolas Jolicoeur - Conseiller d’arrondissement</span>
                    <div class="hero-follow__links">
                        <a
                            class="hero-follow__link"
                            href="https://instagram.com/jolicoeur.mtl"
                            target="_blank"
                            rel="noopener noreferrer"
                        >Instagram <span aria-hidden="true">✱</span></a>
                        <a
                            class="hero-follow__link"
                            href="https://www.facebook.com/jolicoeur.mtl/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >Facebook <span aria-hidden="true">✱</span></a>
                        <a
                            class="hero-follow__link"
                            href="https://www.tiktok.com/@jolicoeur.mtl"
                            target="_blank"
                            rel="noopener noreferrer"
                        >TikTok <span aria-hidden="true">✱</span></a>
                    </div>
                </div>
                <div class="hero__cta">
                    <v-btn
                        class="carto-btn carto-btn--solid"
                        @click="startMap(montrealMap)"
                    >
                        Explorer tout Montréal
                    </v-btn>
                    <v-btn class="carto-btn carto-btn--outline" href="#arrondissements">
                        Choisir un arrondissement
                    </v-btn>
                </div>
            </div>
            <div class="hero__art" aria-hidden="true">
                <div class="hero__art-inner">
                    <div class="hero-phone">
                        <div class="hero-phone__screen">
                            <div class="hero-phone__top">
                                <img
                                    src="/img/hero-streetview.png"
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div class="hero-phone__bottom">
                                <img
                                    src="/img/cartemu.png"
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div class="hero-phone__confirm" aria-hidden="true">
                                    Confirmer
                                </div>
                            </div>
                        </div>
                    </div>

                    <p class="hero__howto">
                        Cliquez sur la carte à l’endroit où vous penser être dans Google Street View.
                        Vous avez 30 secondes pour vous repérer.
                        Plus vous visez proche et plus vous cliquez vite, plus vous faites de points.
                    </p>
                </div>
            </div>
        </section>

        <section id="arrondissements" class="arr-section">
            <div class="section-title">
                <h2>Zone géographique</h2>
                <p>Choisissez un arrondissement ou tout Montréal, et lancez une partie.</p>
            </div>
            <div class="arr-picker">
                <ArrondissementMapPicker
                    v-if="montrealMap"
                    class="arr-picker__map"
                    :geojson-url="montrealMap.url"
                    path-key="NOM"
                    :value="selectedArrondissementName"
                    @input="onArrondissementPicked"
                    :can-play="!!selectedArrondissementMap"
                    @play="selectedArrondissementMap && startMap(selectedArrondissementMap)"
                    @playMontreal="startMap(montrealMap)"
                />
            </div>
        </section>

        <section id="palmares" class="leader-section">
            <div class="section-title">
                <h2>Palmarès</h2>
            </div>
            <div class="leader-wrap">
                <HomeLeaderboard
                    :zone-maps="leaderboardMaps"
                    :default-map-id="montrealMap ? montrealMap.id : null"
                    :limit="15"
                />
            </div>
        </section>

        <section id="apropos" class="about-section">
            <div class="section-title">
                <h2>À propos</h2>
                <p>
                    J'ai créé ce jeu pour savoir à quel point je connaissais bien les quartiers que je représente dans le Sud-Ouest à Montréal.
                    J'ai décidé d'étendre le jeu à tout Montréal, et de le rendre public!
                </p>
            </div>
            <div class="about-wrap">
                <div class="hero__profile carto-card carto-card--white">
                    <div class="profile__main">
                        <div class="profile__photo">
                            <img
                                v-if="profileImage"
                                :src="profileImage"
                                alt="Nicolas Jolicoeur"
                            />
                            <span v-else>NJ</span>
                        </div>
                        <div class="profile__info">
                            <h2>Nicolas Jolicoeur</h2>
                            <p>Conseiller d'arrondissement — Le Sud-Ouest</p>
                            <v-btn
                                class="carto-btn carto-btn--outline"
                                href="https://jolicoeurmtl.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                jolicoeurmtl.com
                            </v-btn>
                        </div>
                    </div>
                    <aside class="profile__follow" aria-label="Me suivre">
                        <h3 class="follow__title">Me suivre</h3>
                        <nav class="follow__links" aria-label="Réseaux sociaux">
                            <div class="follow__item">
                                <a
                                    class="follow__link"
                                    href="https://instagram.com/jolicoeur.mtl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >Instagram</a>
                                <span class="follow__star" aria-hidden="true">✱</span>
                            </div>
                            <div class="follow__item">
                                <a
                                    class="follow__link"
                                    href="https://www.facebook.com/jolicoeur.mtl/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >Facebook</a>
                                <span class="follow__star" aria-hidden="true">✱</span>
                            </div>
                            <div class="follow__item">
                                <a
                                    class="follow__link"
                                    href="https://www.tiktok.com/@jolicoeur.mtl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >TikTok</a>
                                <span class="follow__star" aria-hidden="true">✱</span>
                            </div>
                        </nav>
                    </aside>
                </div>
                <p class="about__attribution">
                    Portions du code basées sur
                    <a
                        href="https://github.com/GeoGuess/GeoGuess"
                        target="_blank"
                        rel="noopener noreferrer"
                    >GeoGuess</a>
                    (licence MIT).
                </p>
            </div>
        </section>

    </ContentPage>
</template>

<script>
import ContentPage from '@/components/page/ContentPage';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { GeoMapType } from '../models/GeoMap';
import { GAME_MODE, SCORE_MODE } from '../constants';
import { SETTINGS_SET_GAME_SETTINGS } from '@/store/mutation-types';
import ArrondissementMapPicker from '@/components/home/ArrondissementMapPicker.vue';
import HomeLeaderboard from '@/components/home/HomeLeaderboard.vue';

export default {
    components: {
        ContentPage,
        ArrondissementMapPicker,
        HomeLeaderboard,
    },
    data() {
        return {
            profileImage: `${process.env.BASE_URL || '/'}img/njp.png`,
            selectedArrondissementName: null,
        };
    },
    computed: {
        ...mapGetters(['maps']),
        baseMaps() {
            return this.maps.filter((map) => map.type !== GeoMapType.Custom);
        },
        montrealMap() {
            return this.baseMaps.find((map) => map.id === 'mtl-arrondissements');
        },
            arrondissementMaps() {
                return this.baseMaps
                    .filter((map) => map.id !== 'mtl-arrondissements')
                    .slice()
                    .sort((a, b) =>
                        (a.nameLocate || a.name || '').localeCompare(
                            b.nameLocate || b.name || ''
                        )
                    );
            },
            leaderboardMaps() {
                // Include "Tout Montreal" as a selectable zone in the leaderboard dropdown.
                if (!this.montrealMap) return this.arrondissementMaps;
                const montreal = Object.assign({}, this.montrealMap, {
                    nameLocate: 'Tout Montréal',
                });
                return [montreal, ...this.arrondissementMaps];
            },
            selectedArrondissementMap() {
                if (!this.selectedArrondissementName) return null;
                return (
                    this.arrondissementMaps.find(
                        (m) => (m.nameLocate || m.name) === this.selectedArrondissementName
                ) || null
            );
        },
    },
    async mounted() {
        await this.getListMaps();
        await this.getListMapsCustoms();
    },
    methods: {
        ...mapActions(['getListMaps', 'getListMapsCustoms', 'loadMap']),
        ...mapActions('settingsStore', ['setSettings']),
        ...mapMutations('settingsStore', {
            setGameSettings: SETTINGS_SET_GAME_SETTINGS,
        }),
            async startMap(map) {
                if (!map) return;
                await this.loadMap(map);
            this.setGameSettings({
                modeSelected: GAME_MODE.CLASSIC,
                time: 30,
                timeAttackSelected: false,
                zoomControl: true,
                moveControl: true,
                panControl: true,
                countdown: 0,
                scoreMode: SCORE_MODE.TIME,
                allPanorama: false,
                optimiseStreetView: true,
                nbRoundSelected: 5,
                });
                await this.setSettings();
            },
        onArrondissementPicked(arrName) {
            this.selectedArrondissementName = arrName;
        },
        scrollTo(id) {
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.home-page {
    background: var(--v-page-base);
}

.hero {
    display: grid;
    gap: 2rem;
    padding: 3.5rem 5% 2rem;
    /* Keep the hero aligned with the same centered column as other sections. */
    max-width: 820px;
    margin: 0 auto;
    grid-template-columns: 1fr;
    grid-template-areas:
        "copy"
        "art";
}

.hero__copy {
    grid-area: copy;
    position: relative;
    z-index: 1; /* Keep copy above the mock if it ever overflows on mobile. */
}

.hero__art {
    grid-area: art;
    position: relative;
    z-index: 0;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.25em;
    font-size: 0.75rem;
    font-weight: 700;
}

.hero__title {
    margin: 0.3rem 0 0.8rem;
}

.hero__wordmark {
    display: inline-block;
    font-size: clamp(3.2rem, 7vw, 5.6rem);
    font-weight: 900;
    letter-spacing: -0.04em;
    line-height: 0.95;
    text-transform: none;
}

.lede {
    font-size: 1.1em;
    max-width: 520px;
}

.hero-follow {
    margin-top: 0.9rem;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.45rem;
    padding: 0.7rem 1.1rem 0.75rem;
    border: 2px solid var(--brand-border);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.55);
    max-width: 100%;
}

.theme--dark .hero-follow {
    background: rgba(20, 20, 20, 0.5);
}

.hero-follow__label {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    opacity: 0.88;
    line-height: 1.2;
}

.hero-follow__links {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.hero-follow__link {
    color: var(--brand-ink);
    text-decoration: none;
    font-weight: 700;
    font-size: 0.88rem;
    line-height: 1;
    border-bottom: 2px solid currentColor;
    padding-bottom: 0.1rem;
}

.hero-follow__link span {
    font-size: 1.08em;
    font-weight: 900;
}

.hero__cta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1.2rem;
}

.hero__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 1.6rem;
}

.hero__art {
    display: grid;
    place-items: center;
}

.hero__art-inner {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    align-items: center;
}

.hero__howto {
    margin: 0;
    max-width: 520px;
    color: var(--brand-ink);
    opacity: 0.85;
    font-weight: 700;
    font-size: 1.1rem;
    line-height: 1.35;
}

.hero-phone {
    /* Keep the mock smaller so it doesn't dominate the hero on mobile/desktop. */
    width: clamp(220px, 42vw, 320px);
    aspect-ratio: 9 / 16;
    border-radius: 40px;
    border: 2px solid var(--brand-border);
    background: #f7f2ea;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
    padding: 14px;
    transform: rotate(-1.1deg);
}

.theme--dark .hero-phone {
    background: #1b1b1b;
    box-shadow: 0 20px 44px rgba(0, 0, 0, 0.45);
}

@media (max-width: 480px) {
    .hero {
        padding: 2.75rem 5% 1.5rem;
        gap: 1.6rem;
    }

    /* Keep the mock-up from taking over the entire first screen on phones. */
    .hero-phone {
        width: min(240px, 84vw);
        transform: rotate(-0.6deg);
    }

    .hero__howto {
        font-size: 1rem;
        line-height: 1.4;
        text-align: left;
        max-width: 34rem;
    }
}

.hero-phone__screen {
    height: 100%;
    width: 100%;
    border-radius: 28px;
    overflow: hidden;
    background: #0b0f14;
    display: grid;
    /* Map should be as prominent (or more) than Street View. */
    grid-template-rows: 48% 52%;
}

.hero-phone__top {
    position: relative;
    background: #0b0f14;
}

.hero-phone__top img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    /* Show a bit lower in the Street View image (less sky, more street). */
    object-position: center 72%;
    filter: saturate(1.05) contrast(1.02);
}

.hero-phone__bottom {
    position: relative;
    background: #f7fbff;
    background-image:
        linear-gradient(rgba(23, 23, 23, 0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgba(23, 23, 23, 0.08) 1px, transparent 1px);
    background-size: 44px 44px;
    overflow: hidden;
}

.theme--dark .hero-phone__bottom {
    background: #202020;
    background-image:
        linear-gradient(rgba(247, 242, 236, 0.07) 1px, transparent 1px),
        linear-gradient(90deg, rgba(247, 242, 236, 0.07) 1px, transparent 1px);
}

.hero-phone__bottom img {
    width: 100%;
    height: calc(100% - 56px);
    display: block;
    /* Fill the phone width (no white bands); allow cropping. */
    object-fit: cover;
    object-position: center;
}

.hero-phone__confirm {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 56px;
    display: grid;
    place-items: center;
    background: #f44336;
    color: #fff;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    border-top: 2px solid var(--brand-border);
}

.hero__profile {
    display: grid;
    grid-template-columns: 1fr;
    padding: 0;
    overflow: hidden; /* keep the follow panel clipped to the card radius */
}

.profile__main {
    display: grid;
    gap: 1.5rem;
    padding: 1.8rem;
    align-items: center;
}

.profile__photo {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 2px solid var(--brand-border);
    display: grid;
    place-items: center;
    font-size: 2rem;
    font-weight: 700;
    background: var(--brand-sand);
    overflow: hidden;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.profile__info h2 {
    margin: 0 0 0.5rem;
    font-size: 1.6rem;
}

.profile__follow {
    background: var(--brand-sand);
    padding: 1.8rem;
    border-top: 2px solid var(--brand-border);
    display: grid;
    align-content: start;
    gap: 1.1rem;
}

.follow__title {
    margin: 0;
    font-size: 2.3rem;
    letter-spacing: -0.02em;
}

.follow__links {
    display: grid;
    gap: 0.9rem;
}

.follow__item {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
}

.follow__link {
    color: var(--brand-ink);
    font-weight: 800;
    font-size: 1.55rem;
    text-decoration: underline;
    text-decoration-thickness: 3px;
    text-underline-offset: 8px;
    line-height: 1.1;
}

.follow__link:hover {
    opacity: 0.9;
}

.follow__star {
    color: var(--brand-ink);
    font-weight: 900;
    font-size: 2.1rem;
    line-height: 1;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        'Liberation Mono', 'Courier New', monospace;
}

.about__attribution {
    margin: 1.75rem auto 0;
    font-size: 0.95rem;
    line-height: 1.25;
    opacity: 0.85;
    max-width: 760px;
    text-align: center;
}

.about__attribution a {
    color: inherit;
    text-decoration: none;
}

.section-title {
    padding: 0 5%;
    max-width: 820px;
    margin: 0 auto 1.5rem;
}

.section-title h2 {
    font-size: 2.2rem;
    margin-bottom: 0.4rem;
}

.arr-section {
    padding: 2rem 0 3rem;
}

.arr-picker {
    padding: 0 5%;
    display: grid;
    gap: 1rem;
    max-width: 820px;
    margin: 0 auto;
}

.leader-section {
    padding: 2rem 0 4rem;
}

.leader-wrap {
    padding: 0 5%;
    max-width: 820px;
    margin: 0 auto;
}

.about-section {
    padding: 2rem 0 5rem;
}

.about-wrap {
    padding: 0 5%;
}

.about-wrap .hero__profile {
    max-width: 760px;
    margin: 0 auto;
}

@media (min-width: 960px) {
    .hero {
        align-items: start;
    }

    .hero__art {
        place-items: start;
    }

    .hero__art-inner {
        flex-direction: row;
        align-items: flex-start;
        gap: 2rem;
    }

    .hero__howto {
        max-width: 360px;
        margin-top: 64px;
    }

    .hero__profile {
        grid-template-columns: 1fr 320px;
        align-items: stretch;
    }

    .profile__main {
        grid-template-columns: 160px 1fr;
    }

    .profile__follow {
        border-top: 0;
        border-left: 2px solid var(--brand-border);
        padding: 2.2rem;
    }

    .arr-picker {
        grid-template-columns: 1fr;
        align-items: start;
    }
}
</style>
